import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTRPC } from '#src/trpc.js';
import { sigmaParameterLabel } from '#utils/sigma-parameter-label.js';

import type { HilPackage, HilSigmaModelDefinitionParameterEntry } from '@cadstrom/backend/shared';
import type { Metadata, ProjectModule } from '@cadstrom/sigma-engine-shared';

/** Metadata keys that are rendered elsewhere on the card (or are noise) and excluded from the parameter grid. */
const hiddenMetadataKeys = new Set(['value']);

interface ParameterEntry {
	label: string;
	value: string;
	/** When present, the entry is a Sigma Model parameter and its definition powers the info tooltip. */
	parameterDefinition?: HilSigmaModelDefinitionParameterEntry;
}

export type ModuleCardOverviewTabProps = {
	pkg: HilPackage | null;
	projectModule: ProjectModule | undefined;
};

export function ModuleCardOverviewTab({ pkg, projectModule }: ModuleCardOverviewTabProps) {
	const trpc = useTRPC();

	const { data } = useQuery({
		...trpc.misc.getSigmaModelDefinitions.queryOptions({}, { staleTime: Infinity }),
		enabled: pkg !== null,
	});
	const sigmaModelDefinitions = data?.sigmaModelDefinitions;

	const parameterEntries: ParameterEntry[] = useMemo(() => {
		if (pkg) {
			const definition = sigmaModelDefinitions?.find((d) => d.name === pkg.sigmaModelType);
			return Object.entries(pkg.sigmaModelParameters)
				.filter(([, valueVariance]) => valueVariance.value !== null && valueVariance.value !== '')
				.map(([name, valueVariance]) => {
					const parameterDefinition = definition?.parameters.find((p) => p.name === name);
					const unit = parameterDefinition?.units?.short;
					return {
						label: sigmaParameterLabel(name),
						value: `${valueVariance.value}${unit !== undefined ? ` ${unit}` : ''}${
							valueVariance.variance !== null && valueVariance.variance !== '' ? ` ±${valueVariance.variance}` : ''
						}`,
						isValueParameter: parameterDefinition?.isValueParameter ?? false,
						parameterDefinition,
					};
				})
				.toSorted((a, b) => {
					const valueParameterOrder = Number(b.isValueParameter) - Number(a.isValueParameter);
					return valueParameterOrder !== 0 ? valueParameterOrder : a.label.localeCompare(b.label);
				});
		} else {
			const entries: ParameterEntry[] = [];
			if (projectModule?.model?.modelId !== undefined) {
				entries.push({ label: 'Component type', value: projectModule.model.modelId });
			}
			const resolvedValue =
				projectModule?.value !== undefined && projectModule.value !== '' ?
					projectModule.value
				:	getMetadataString(projectModule?.metadata, 'value');
			if (resolvedValue !== undefined && resolvedValue !== '') {
				entries.push({ label: 'Value', value: resolvedValue });
			}
			for (const [key, value] of Object.entries(projectModule?.metadata ?? {})) {
				if (hiddenMetadataKeys.has(key.toLowerCase()) || key.startsWith('cadstrom.')) {
					continue;
				}
				if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
					entries.push({ label: key, value: String(value) });
				}
			}
			return entries;
		}
	}, [pkg, projectModule, sigmaModelDefinitions]);

	return (
		<div className='m-2 flex flex-col'>
			{parameterEntries.length === 0 ?
				<p className='text-gray-a8 italic'>No parameters available</p>
			:	<div className='overflow-hidden rounded-sm border border-gray-4 bg-gray-a4'>
					<div className='grid min-h-0 grid-cols-2 gap-px'>
						{parameterEntries.map((entry) => (
							<div key={entry.label} className='flex min-w-0 flex-col gap-2 bg-gray-2 p-2'>
								<div className='-my-1 flex min-w-0 items-center gap-1 py-1 leading-tight text-gray-a10'>
									<p>{entry.label}</p>
									{entry.parameterDefinition ?
										<Tooltip
											enterDelay={700}
											title={
												<div className='flex flex-col gap-2'>
													<div className='flex flex-wrap items-center gap-2'>
														<p className='font-mono'>{entry.parameterDefinition.name}</p>
														<div className='flex gap-2'>
															{entry.parameterDefinition.units ?
																<Chip
																	label={`${entry.parameterDefinition.units.long} (${entry.parameterDefinition.units.short})`}
																/>
															:	null}
														</div>
													</div>
													<p className='mb-1 text-gray-a11'>{entry.parameterDefinition.description}</p>
												</div>
											}
										>
											<InfoOutlinedIcon className='shrink-0 cursor-help text-base text-gray-a11' />
										</Tooltip>
									:	null}
								</div>
								<p className='truncate -my-1 py-1 tabular-nums' title={entry.value}>
									{entry.value}
								</p>
							</div>
						))}
					</div>
				</div>
			}
		</div>
	);
}

/**
 * Reads a string-valued entry from design metadata.
 *
 * @param metadata - The metadata record to read from, if any
 * @param key - The metadata key to look up
 * @returns The string value, or undefined when absent or not a string
 */
function getMetadataString(metadata: Metadata | undefined, key: string): string | undefined {
	if (metadata === undefined) {
		return undefined;
	}
	const normalizedKey = key.toLowerCase();
	const entry = Object.entries(metadata).find(([k]) => k.toLowerCase() === normalizedKey);
	const value = entry?.[1];
	return value !== undefined && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') ?
			String(value)
		:	undefined;
}
